import { startCase } from 'lodash';
import { ReactNode } from 'react';

import {
  EnhancedUsage,
  EnhancedUsageType,
  Feature,
} from 'dashboard/models/inquiry-template-version';

const readableFeature = (feature: Feature) => {
  switch (feature) {
    case Feature.Step:
      return 'Screen';
    case Feature.Conditional:
      return 'Branching';
    case Feature.ConditionalBranch:
      return 'Route';
    case Feature.Asset:
      return 'Non-Start-Screen Asset';
    default:
      return startCase(feature);
  }
};

export const parseEnhancedUsage = (usage: EnhancedUsage, includeUsageCounts: boolean): string => {
  if (usage.type !== EnhancedUsageType.QuotaExceeded) return '';

  type ModifyParsedStringWithUsagesCountParams = {
    baseString: string;
    includeUsageCounts: boolean;
    usagesCountQuota: number;
  };

  const modifyParsedStringWithUsagesCount = (
    params: ModifyParsedStringWithUsagesCountParams,
  ): string => {
    const { includeUsageCounts, baseString, usagesCountQuota } = params;
    return includeUsageCounts ? `${baseString} (${usagesCountQuota} used)` : baseString;
  };

  const baseParamsForEvaluation: ModifyParsedStringWithUsagesCountParams = {
    baseString: ![Feature.Asset, Feature.HeaderLogo].includes(usage.feature)
      ? `Exceeded ${usage['usage-count-quota']} ${readableFeature(usage.feature)}${
          usage['usage-count-quota'] === 1 ? '' : 's'
        }`
      : '',
    includeUsageCounts,
    usagesCountQuota: usage['usage-count'],
  };

  switch (usage.feature) {
    case Feature.Asset:
      return modifyParsedStringWithUsagesCount({
        ...baseParamsForEvaluation,
        baseString: `Added an uploaded Image Asset to a non-start screen`,
        includeUsageCounts,
      });

    case Feature.HeaderLogo:
      return modifyParsedStringWithUsagesCount({
        ...baseParamsForEvaluation,
        baseString: `Added an uploaded Image Asset to the main navigation`,
        includeUsageCounts: false, // This assumes the quota is 0
      });

    case Feature.ConditionalBranch:
      return modifyParsedStringWithUsagesCount({
        ...baseParamsForEvaluation,
        baseString: `${baseParamsForEvaluation.baseString} of any Branching`,
      });

    default:
      return modifyParsedStringWithUsagesCount(baseParamsForEvaluation);
  }
};

interface InquiriesEnhancedUsagesListProps {
  enhancedUsages: EnhancedUsage[];
  message: ReactNode;
  includeUsageCounts?: boolean;
}

const InquiriesEnhancedUsagesList = ({
  enhancedUsages,
  message,
  includeUsageCounts = true,
}: InquiriesEnhancedUsagesListProps) => {
  return (
    <>
      {message}
      <ul style={{ listStylePosition: 'inside' }}>
        {enhancedUsages.map((enhancedUsage, i) => {
          return <li key={i}>{parseEnhancedUsage(enhancedUsage, includeUsageCounts)}</li>;
        })}
      </ul>
    </>
  );
};

export default InquiriesEnhancedUsagesList;
